import { AxiosResponse } from 'axios';
import { format } from 'date-fns';
import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';

import {
  type ChainStoreApiDateParams,
  resolveChainStoreComparisonGraphEndDateSource,
} from '../domain/chain/date';
import { validateChainStoreKiGraphParams } from '../domain/chain/validation';
import {
  ComparisonGraph,
  ComparisonGraphFieldItem,
  ComparisonGraphParams,
  ComparisonGraphResponse,
  toComparisonGraphSetting,
} from '../domain/comparisonGraph';
import {
  normalizeComparisonGraphFieldsForFetch,
  resolveComparisonGraphFields,
  resolveComparisonGraphFieldsForSearch,
} from '../domain/comparisonGraphFields';
import { DataChainStoreKiParams } from '../domain/dataChainStoreKi';
import { GRAPH_DATE_TYPE, GraphDateType } from '../domain/dataKiGraph';
import { ShuOption } from '../domain/shu';

import { dataChainStoreKiDataSearchConditionSelector } from '../redux/server/dataChainStoreKi';
import {
  ChangeDataChainStoreKiGraphDateType,
  ChangeDataChainStoreKiGraphField,
  DataChainStoreKiGraphActionCreators,
  DataChainStoreKiGraphActionTypes,
  FetchDataChainStoreKiGraphAction,
  SearchDataChainStoreKiGraph,
  SetDataChainStoreKiGraphFieldsAction,
  dataChainStoreKiGraphSearchConditionSelector,
} from '../redux/server/dataChainStoreKiGraph';
import { chainStoreReportsApiDateParamsSelector } from '../redux/ui/chainStoreReportsSetting';
import { Api, buildConfig } from '../utils/api';
import {
  calcEndDateWithExcludeToday,
  calcStartDateFromDateType,
} from '../utils/date';
import { handleErrorSaga } from './errorSaga';

type GraphFieldFetchSuccess = {
  field: string;
  response: ComparisonGraphResponse;
};

type GraphFieldFetchFailure = {
  field?: string;
  error: unknown;
};

type GraphFieldFetchResult = GraphFieldFetchSuccess | GraphFieldFetchFailure;

const isGraphFieldFetchSuccess = (
  result: GraphFieldFetchResult
): result is GraphFieldFetchSuccess => 'response' in result;

const isGraphFieldFetchFailure = (
  result: GraphFieldFetchResult
): result is GraphFieldFetchFailure => 'error' in result;

const toGraphApiParams = (
  params: ComparisonGraphParams,
  field: string
): ComparisonGraphParams => {
  const { field: _field, fields: _fields, ...commonParams } = params;
  return { ...commonParams, field };
};

const buildComparisonGraph = (
  results: GraphFieldFetchSuccess[]
): ComparisonGraph => {
  const firstResponse = results.at(0)?.response;
  if (!firstResponse) {
    throw new Error('Chain store ki graph API response is empty');
  }
  const graphList: ComparisonGraphFieldItem[] = results.map(
    ({ field, response }) => ({
      field,
      data: response.data,
    })
  );
  return {
    setting: toComparisonGraphSetting(firstResponse.setting),
    graphList,
  };
};

function* fetchGraphByField(
  api: Api,
  params: ComparisonGraphParams,
  field: string
) {
  const apiParams = toGraphApiParams(params, field);
  validateChainStoreKiGraphParams(apiParams);
  const response: AxiosResponse<ComparisonGraphResponse> = yield call(
    api.get,
    '/data/chainStore/ki/graph',
    buildConfig(apiParams)
  );
  return response.data;
}

function* fetchGraphByFieldSafely(
  api: Api,
  params: ComparisonGraphParams,
  field: string
): Generator<unknown, GraphFieldFetchResult, ComparisonGraphResponse> {
  try {
    const response = yield call(fetchGraphByField, api, params, field);
    return { field, response };
  } catch (error: unknown) {
    return { field, error };
  }
}

function* hideDataChainStoreKiGraphOnFailure(shuCode: string) {
  yield put(
    DataChainStoreKiGraphActionCreators.hideDataChainStoreKiGraphAction(shuCode)
  );
}

/**
 * 検索条件を元に推移グラフ（機種比較）のデータを取得する
 */
export function* fetchDataChainStoreKiGraphSaga(
  api: Api,
  action: FetchDataChainStoreKiGraphAction
) {
  const { shuCode, params } = action.payload;
  const fields = resolveComparisonGraphFields(params);

  yield put(
    DataChainStoreKiGraphActionCreators.fetchDataChainStoreKiGraphRequestAction(
      shuCode
    )
  );

  const results: GraphFieldFetchResult[] = yield all(
    fields.map((field) => call(fetchGraphByFieldSafely, api, params, field))
  );
  const successfulResults = results.filter(isGraphFieldFetchSuccess);
  const failedResults = results.filter(isGraphFieldFetchFailure);

  if (successfulResults.length === 0) {
    yield call(hideDataChainStoreKiGraphOnFailure, shuCode);
    const firstError = failedResults.at(0)?.error;
    if (firstError !== undefined) {
      yield fork(handleErrorSaga, firstError);
    }
    return;
  }

  try {
    yield put(
      DataChainStoreKiGraphActionCreators.fetchDataChainStoreKiGraphSuccessAction(
        shuCode,
        buildComparisonGraph(successfulResults)
      )
    );
  } catch (error: unknown) {
    yield call(hideDataChainStoreKiGraphOnFailure, shuCode);
    yield fork(handleErrorSaga, error);
    return;
  }

  if (failedResults.length > 0) {
    console.warn(
      `[dataChainStoreKiGraphSaga] 一部グラフ項目の取得に失敗しました (${shuCode}): ${failedResults
        .map((result) => result.field ?? 'unknown')
        .join(', ')}`
    );
  }
}

function* refetchDataChainStoreKiGraphWithFields(
  shu: ShuOption,
  fields: string[]
) {
  const searchCondition: ComparisonGraphParams = yield select(
    dataChainStoreKiGraphSearchConditionSelector(shu)
  );
  if (Object.keys(searchCondition).length === 0) {
    console.warn(
      `[dataChainStoreKiGraphSaga] グラフ検索条件が未設定のため再取得をスキップしました (${shu.code})`
    );
    return;
  }
  yield put(
    DataChainStoreKiGraphActionCreators.fetchDataChainStoreKiGraphAction(
      shu.code,
      {
        ...searchCondition,
        fields: normalizeComparisonGraphFieldsForFetch(fields),
      }
    )
  );
}

/**
 * チェックボックスで選択した機種の推移グラフ（機種比較）データを取得する（推移グラフボタン押下時）
 */
export function* searchDataChainStoreKiGraphSaga(
  action: SearchDataChainStoreKiGraph
) {
  const { shu, kiList } = action.payload;

  const prevParams: ComparisonGraphParams = yield select(
    dataChainStoreKiGraphSearchConditionSelector(shu)
  );
  const fields = resolveComparisonGraphFieldsForSearch(prevParams);
  const { dateType } = prevParams;

  const searchCondition: DataChainStoreKiParams = yield select(
    dataChainStoreKiDataSearchConditionSelector(shu.code)
  );
  const apiDateParams: ChainStoreApiDateParams | undefined = yield select(
    chainStoreReportsApiDateParamsSelector
  );

  // テーブル API と同じ日付ソースを使う。ymdList だけ見ると start/end 送信時に当日へ落ちる
  const endDate = calcEndDateWithExcludeToday(
    !!searchCondition.excludeToday,
    resolveChainStoreComparisonGraphEndDateSource(apiDateParams, {
      ymdList: searchCondition.ymdList,
      ymdComparisonList: searchCondition.ymdComparisonList,
      endDate: searchCondition.endDate,
    })
  );
  const startDate = calcStartDateFromDateType(
    dateType ?? GRAPH_DATE_TYPE.DAILY,
    endDate
  );

  yield put(
    DataChainStoreKiGraphActionCreators.fetchDataChainStoreKiGraphAction(
      shu.code,
      {
        kiList,
        shuList: searchCondition.shuList,
        shuGroupIds: searchCondition.shuGroupIds,
        halls: searchCondition.halls,
        excludeToday: searchCondition.excludeToday,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        ...(dateType ? { dateType } : {}),
        fields,
      }
    )
  );
}

function* changeDataChainStoreKiGraphFieldSaga(
  action: ChangeDataChainStoreKiGraphField
) {
  const { shu, field } = action.payload;
  yield* refetchDataChainStoreKiGraphWithFields(shu, [field]);
}

function* setDataChainStoreKiGraphFieldsSaga(
  action: SetDataChainStoreKiGraphFieldsAction
) {
  const { shu, fields } = action.payload;
  yield* refetchDataChainStoreKiGraphWithFields(shu, fields);
}

function* changeDataChainStoreKiGraphDateTypeSaga(
  action: ChangeDataChainStoreKiGraphDateType
) {
  const { shu, dateType } = action.payload;

  const searchCondition: ComparisonGraphParams = yield select(
    dataChainStoreKiGraphSearchConditionSelector(shu)
  );
  const fields = resolveComparisonGraphFields(searchCondition);

  const endDate = calcEndDateWithExcludeToday(
    !!searchCondition.excludeToday,
    searchCondition.endDate
  );
  const startDate = calcStartDateFromDateType(
    dateType as GraphDateType,
    endDate
  );

  yield put(
    DataChainStoreKiGraphActionCreators.fetchDataChainStoreKiGraphAction(
      shu.code,
      {
        ...searchCondition,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        dateType: dateType as GraphDateType,
        fields,
      }
    )
  );
}

function* handleFetchDataChainStoreKiGraphSaga(api: Api) {
  yield takeEvery(
    DataChainStoreKiGraphActionTypes.FETCH_DATA_CHAIN_STORE_KI_GRAPH,
    fetchDataChainStoreKiGraphSaga,
    api
  );
}

function* handleSearchDataChainStoreKiGraphSaga() {
  yield takeEvery(
    DataChainStoreKiGraphActionTypes.SEARCH_DATA_CHAIN_STORE_KI_GRAPH,
    searchDataChainStoreKiGraphSaga
  );
  yield takeEvery(
    DataChainStoreKiGraphActionTypes.CHANGE_DATA_CHAIN_STORE_KI_GRAPH_FIELD,
    changeDataChainStoreKiGraphFieldSaga
  );
  yield takeEvery(
    DataChainStoreKiGraphActionTypes.CHANGE_DATA_CHAIN_STORE_KI_GRAPH_DATE_TYPE,
    changeDataChainStoreKiGraphDateTypeSaga
  );
  yield takeEvery(
    DataChainStoreKiGraphActionTypes.SET_DATA_CHAIN_STORE_KI_GRAPH_FIELDS,
    setDataChainStoreKiGraphFieldsSaga
  );
}

export function* dataChainStoreKiGraphSaga(context: { api: Api }) {
  yield fork(handleFetchDataChainStoreKiGraphSaga, context.api);
  yield fork(handleSearchDataChainStoreKiGraphSaga);
}
